import React from 'react';
import {
  CContainer,
  CCard,
  CCardBody,
  CCardText,
  CCardTitle,
  CButton,
  CFormInput,
  CRow,
  CCol,
  CFormLabel,
} from '@coreui/react-pro';
import {
  Map,
  useMap,
  AdvancedMarker,
  Pin,
  MapControl,
  ControlPosition,
} from '@vis.gl/react-google-maps';
import {
  initYieldEstimatorInterface,
  findMe,
  updateYieldEstimatorGeolocation,
  setYieldEstimatorPlantPopulation,
  setYieldEstimatorKernelsPerBushel,
  setYieldEstimatorEarsRows,
  setYieldEstimatorEarsLength,
  setYieldEstimatorFile,
  calculateYield,
  setYieldEstimatorGrowerIdOverride,
} from './tool-box-actions';
import {
  useDispatch,
  useSelector,
} from 'react-redux';

const YieldEstimator = () => {

  const dispatch = useDispatch();

  const map = useMap();

  const {
    sidebarVisible,
    sidebarUnfoldable,
  } = useSelector(state => state.userSettings);

  const {
    yieldEstimatorLatitude,
    yieldEstimatorLongitude,
    plantPopulation,
    kernelsPerBushel,
    ears,
    calculatedYield,
    yieldEstimatorFileToUpload,
    yieldEstimatorGrowerIdOverride,
  } = useSelector(state => state.toolBox);

  const isYieldEstimatorUser = useSelector(state => state.auth?.authorization?.isYieldEstimatorUser);

  const [marker, setMarker] = React.useState(null);

  React.useEffect(() => {
    dispatch(initYieldEstimatorInterface());
  }, [dispatch]); 

  React.useEffect(() => {
    if (yieldEstimatorLatitude !== 0 && yieldEstimatorLongitude !== 0 && map !== null) {
      setMarker({
        position: { lat: yieldEstimatorLatitude, lng: yieldEstimatorLongitude },
        key: Date.now(),
      });
      map.panTo({lat: yieldEstimatorLatitude, lng: yieldEstimatorLongitude});
      if (map.getZoom() < 16) {
        map.setZoom(16);
      }
    }
  }, [yieldEstimatorLatitude, yieldEstimatorLongitude, map]);

  const fileInputRef = React.useRef(null);

  const handleTakePictureButtonClick = () => {
    fileInputRef.current.click();
  };

  React.useEffect(() => {
    if (yieldEstimatorFileToUpload === null) {
      fileInputRef.current.value = '';
    }
  }, [yieldEstimatorFileToUpload]);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const height = windowHeight - 64;

  let width = windowWidth;

  if (sidebarUnfoldable && sidebarVisible) {
    width -= 64;
  } 
  else if (!sidebarUnfoldable && sidebarVisible) {
    width -= 256;
  }
  
  return (
    <div style={{flexDirection: 'row', display: 'flex',}}>
      <div style={{
        width: isSidebarCollapsed ? 0 : 360,
        height: height,
        transition: 'width 0.3s ease-in-out',
      }}>
        <div style={{
          width: 360,
          height: height,
          overflowY: 'auto',
        }}>
          <CContainer fluid>
            <div style={{height: 10,}} />
            <CCard>
              <CCardBody>
                <CCardTitle>Yield Estimator</CCardTitle>
                <CCardText>
                  The Yield Estimator will attempt to automatically find your location on the map. You can then drag the pin to the field you want to estimate if the location is not correct. Once you have the pin in the correct field, use the Yield Estimator below.
                </CCardText>
                <CRow className='mb-3'>
                  <CCol className='d-flex justify-content-center'>
                    <CButton
                      color='primary'
                      variant='outline'
                      style={{width: 140,}}
                      onClick={() => dispatch(findMe(true))}>
                      Find Me
                    </CButton>
                  </CCol>
                </CRow>
                <CRow className='mb-1'>
                  <CCol className='d-flex justify-content-center'>
                    <CButton
                      color='primary'
                      variant='outline'
                      style={{width: 140,}}
                      onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                      Expand Map
                    </CButton>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
            <div style={{ height: 10, }} />
            <CCard>
              <CCardBody>
                <CRow className='mb-3'>
                  <CCol>
                    <h4 style={{color: '#00244B', fontWeight: 'bolder',}}>Calculated Yield:<br/>{calculatedYield} bu/ac</h4>
                  </CCol>
                </CRow>
                <CRow className='mb-3'>
                  <CCol className='d-flex justify-content-center'>
                    <CButton
                      color='primary'
                      variant='outline'
                      style={{ width: 150, }}
                      onClick={() => dispatch(calculateYield())}>
                      Calculate Yield
                    </CButton>
                  </CCol>
                </CRow>
                <CRow className='mb-3'>
                  <CCol>
                    <CFormLabel>Location</CFormLabel>
                    <p>
                      Lat: {yieldEstimatorLatitude.toFixed(6)}, Lng: {yieldEstimatorLongitude.toFixed(6)}
                    </p>
                  </CCol>
                </CRow>
                <CRow className='mb-3'>
                  <CCol>
                    <CFormInput
                      label='Plant Population (Per Acre)'
                      type='number'
                      value={plantPopulation}
                      onChange={(e) => dispatch(setYieldEstimatorPlantPopulation(e.target.value !== '' ? parseInt(e.target.value) : ''))}
                    />
                  </CCol>
                </CRow>
                <CRow className='mb-3'>
                  <CCol>
                    <CFormInput
                      label='Kernels (Per Bushel)'
                      type='number'
                      value={kernelsPerBushel}
                      onChange={(e) => dispatch(setYieldEstimatorKernelsPerBushel(e.target.value !== '' ? parseInt(e.target.value) : ''))}
                    />
                  </CCol>
                </CRow>
                <CRow className='mb-3'>
                  <CCol>
                    <CRow className='font-weight-bold'>
                      <CCol xs={2}>Ear</CCol>
                      <CCol xs={5}># of Rows</CCol>
                      <CCol xs={5}>Length of Row</CCol>
                    </CRow>
                    {ears.map((ear, index) => (
                      <CRow key={index} className='mb-2'>
                        <CCol xs={2}>{index + 1}</CCol>
                        <CCol xs={5}>
                          <CFormInput
                            type='number'
                            value={ear.rows}
                            onChange={(e) => dispatch(setYieldEstimatorEarsRows(index, e.target.value !== '' ? parseInt(e.target.value) : ''))}
                          />
                        </CCol>
                        <CCol xs={5}>
                          <CFormInput
                            type='number'
                            value={ear.length}
                            onChange={(e) => dispatch(setYieldEstimatorEarsLength(index, e.target.value !== '' ? parseInt(e.target.value) : ''))}
                          />
                        </CCol>
                      </CRow>
                    ))}
                  </CCol>
                </CRow>
                {
                  isYieldEstimatorUser === true ? (
                    <CRow className='mb-4'>
                      <CCol>
                        <CFormInput
                          label='Name'
                          type='text'
                          value={yieldEstimatorGrowerIdOverride}
                          onChange={(e) => dispatch(setYieldEstimatorGrowerIdOverride(e.target.value))}
                        />
                      </CCol>
                    </CRow>
                  ) : null
                }
                <CRow className='mb-1'>
                  <CCol className='d-flex justify-content-center'>
                    <CFormInput
                      ref={fileInputRef}
                      style={{ display: 'none', }}
                      type='file'
                      accept='image/*'
                      capture='environment'
                      onChange={(e) => {
                        dispatch(setYieldEstimatorFile(e.target.files[0]));
                        dispatch(calculateYield());
                      }}
                    />
                    <CButton
                      color='primary'
                      variant='outline'
                      style={{width: 260,}}
                      onClick={handleTakePictureButtonClick}>
                      Take Picture of Sample
                    </CButton>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
            <br />
          </CContainer>
        </div>
      </div>
      <div style={{flexDirection: 'column', display: 'flex',}}>
        <div
          style={{
            width: width - (isSidebarCollapsed ? 0 : 360),
            height: height,
          }}>
          <Map
            mapId='TOOLS'
            defaultCenter={{lng: -88.257311, lat: 40.112519,}}
            defaultZoom={parseInt(process.env.REACT_APP_DEFAULT_MAP_ZOOM)}
            fullscreenControl={false}
            mapTypeControl={true}
            streetViewControl={false}
            mapTypeId='hybrid'
            gestureHandling='greedy'
            disableDefaultUI={false}>
            {
              isSidebarCollapsed === true ? (
                <MapControl position={ControlPosition.TOP_LEFT}>
                  <CButton
                    color='primary'
                    variant='outline'
                    style={{
                      width: 180,
                      backgroundColor: 'white',
                      marginTop: 10,
                      marginLeft: 10,
                      color: 'black',
                    }}
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                    Show Yield Estimator
                  </CButton>
                </MapControl>
              ) : null
            }
            {
              marker != null ? (
                <AdvancedMarker
                  key={marker.key}
                  position={marker.position}
                  draggable={true}
                  onDragEnd={(event) => dispatch(updateYieldEstimatorGeolocation(event.latLng.lat(), event.latLng.lng()))}>
                  <Pin
                    background='#00244B' 
                    borderColor='#FFFFFF'
                    glyphColor='#FFFFFF' 
                    scale={1.5}
                  />
                </AdvancedMarker>
              ) : null
            }
          </Map>
        </div>
      </div>
    </div>
  );
};

export default YieldEstimator;
